import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faForward } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { uniqueBy } from '@sorare/core/src/lib/arrays';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CollectionAnimationCompetition } from './CollectionAnimationCompetition';
import type { CollectionAnimationV2_anyCardInterface } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  position: relative;
  overflow: hidden;
  isolation: isolate;
  height: var(--100vh);
  @media ${tabletAndAbove} {
    max-height: 85vh;
    height: 100%;
  }
  justify-content: stretch;
`;

const SkipButton = styled(IconButton)`
  position: absolute;
  bottom: var(--double-unit);
  right: var(--triple-unit);
  z-index: 10;
`;

const CompetitionsWrapper = styled(Horizontal)`
  transition: transform 0.5s cubic-bezier(0.02, 0.36, 0.34, 0.97);
  flex: 1;
  height: 100%;
`;

type Props = {
  shouldAnimate: boolean;
  newCards: CollectionAnimationV2_anyCardInterface[];
  onAnimationEnd: () => void;
};

export const CollectionAnimationV2 = ({
  shouldAnimate,
  newCards,
  onAnimationEnd: onAnimationEndProp,
}: Props) => {
  const [currentCollectionIndex, setCurrentCollectionIndex] =
    useState<number>();

  if (shouldAnimate && currentCollectionIndex === undefined) {
    setCurrentCollectionIndex(0);
  }

  const competitions = uniqueBy(
    competition => competition.slug,
    newCards
      .map(card => card.anyTeam)
      .filter(isTypeFn('Club'))
      .map(club => club.domesticLeague)
      .filter(Boolean)
  );

  return (
    <Root>
      <CompetitionsWrapper
        style={{
          transform: `translateX(calc(-100% * ${currentCollectionIndex})`,
        }}
        gap={0}
      >
        {competitions.map((competition, index) => {
          const newCardsForCompetition = newCards.filter(card => {
            const { anyTeam } = card;
            return (
              isType(anyTeam, 'Club') &&
              anyTeam?.domesticLeague?.slug === competition.slug
            );
          });
          return (
            <CollectionAnimationCompetition
              key={competition.slug}
              isFirstCompetition={index === 0}
              newCards={newCardsForCompetition}
              competition={competition}
              shouldAnimate={index === currentCollectionIndex}
              onAnimationEnd={() => {
                if (typeof currentCollectionIndex !== 'number') {
                  return;
                }
                if (currentCollectionIndex === competitions.length - 1) {
                  onAnimationEndProp();
                  return;
                }
                setCurrentCollectionIndex(currentCollectionIndex + 1);
              }}
            />
          );
        })}
      </CompetitionsWrapper>
      <SkipButton
        size="medium"
        onClick={onAnimationEndProp}
        color="secondary"
        icon={faForward}
      />
    </Root>
  );
};

CollectionAnimationV2.fragments = {
  anyCardInterface: gql`
    fragment CollectionAnimationV2_anyCardInterface on AnyCardInterface {
      slug
      anyTeam {
        slug
        ... on Club {
          slug
          domesticLeague {
            slug
            ...CollectionAnimationCompetition_competition
          }
        }
      }
      ...CollectionAnimationCompetition_anyCardInterface
    }
    ${CollectionAnimationCompetition.fragments.anyCardInterface}
    ${CollectionAnimationCompetition.fragments.competition}
  ` as TypedDocumentNode<CollectionAnimationV2_anyCardInterface>,
};
